import Chip, { ChipProps } from '@mui/material/Chip';
import { TZDate } from '@date-fns/tz';

import { startOfDayInTZ, endOfDayInTZ } from '#common/utils';

// Picker values are browser-local instants whose calendar day is the day the
// user picked. With a timeZone, that calendar day is re-anchored to the market
// timezone so range filters agree with the server's day bucketing; without
// one, the boundary stays browser-local (pre-market-TZ behavior).
export const normalizeStartOfDay = (date: Date | string | number, timeZone?: string): Date => {
  const local = new Date(date);
  if (!timeZone) {
    local.setHours(0, 0, 0, 0);
    return local;
  }
  return new Date(new TZDate(
    local.getFullYear(),
    local.getMonth(),
    local.getDate(),
    timeZone,
  ).getTime());
};

export const normalizeEndOfDay = (date: Date | string | number, timeZone?: string): Date => {
  const local = new Date(date);
  if (!timeZone) {
    local.setHours(23, 59, 59, 999);
    return local;
  }
  const next = new TZDate(
    local.getFullYear(),
    local.getMonth(),
    local.getDate(),
    timeZone,
  );
  next.setHours(23, 59, 59, 999);
  return new Date(next.getTime());
};

export const getDefaultReminderRange = (timeZone?: string): { from: Date; to: Date } => {
  // "today" means the market's calendar day, so anchor on the instant, not on
  // the browser's local date components
  const end = endOfDayInTZ(timeZone);
  const start = new Date(end);
  start.setMonth(start.getMonth() - 6);
  return {
    from: startOfDayInTZ(timeZone, start),
    to: end,
  };
};

interface ReminderRow {
  shippingReminderStatus?: string;
  shippingReminderDisplayDays?: number;
  shippingReminderDays?: number;
  [key: string]: unknown;
}

interface ReminderMeta {
  label: string;
  color: string;
  backgroundColor: string;
}

export const getReminderMeta = (row?: ReminderRow): ReminderMeta | null => {
  if (!row?.shippingReminderStatus) {
    return null;
  }
  const displayDays = Number(
    row.shippingReminderDisplayDays ?? row.shippingReminderDays ?? 0,
  );
  const absDays = Math.abs(displayDays);
  const pluralize = (value: number) => (value === 1 ? 'day' : 'days');
  if (row.shippingReminderStatus === 'overdue') {
    const label = absDays < 1
      ? 'Overdue (<1 day)'
      : `Overdue by ${absDays} ${pluralize(absDays)}`;
    return {
      label,
      color: 'error',
      backgroundColor: 'rgba(211, 47, 47, 0.1)',
    };
  }
  const futureDays = Math.max(displayDays, 0);
  const label = futureDays < 1
    ? 'Due today'
    : `Due in ${futureDays} ${pluralize(futureDays)}`;
  return {
    label,
    color: 'warning',
    backgroundColor: 'rgba(255, 167, 38, 0.12)',
  };
};

export function ReminderChip({ row }: { row?: ReminderRow }) {
  const meta = getReminderMeta(row);
  if (!meta) {
    return null;
  }
  return (
    <Chip
      size="small"
      color={meta.color as ChipProps['color']}
      label={meta.label}
    />
  );
}
